/**
 * Stammdaten: die Status und ihre Farben (ADR 0005).
 *
 * Der Ortsverband legt selbst fest, welche Status es gibt, wie sie heissen
 * und welche Farbe sie tragen. Zwei Kennzeichen entscheiden, wie sie zaehlen:
 * „einsatzbereit" und „archiviert". Genau einer ist der Standard. Drei Haken
 * regeln Rechte und Verhalten: Nur Admin, Mit Bestaetigung,
 * Stammplatz-Rueckbuchung ignorieren (ADR 0025).
 *
 * Status werden nie geloescht, nur stillgelegt -- alte Buchungen tragen sie
 * weiter. Aendert sich ein Kennzeichen, rechnet der Server den Bestand neu.
 *
 * Nur fuer Administratoren und nur mit Netz (ADR 0006).
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import type { StatusArt } from "@thw/contracts";
import { anfrage, sitzungLesen } from "../../api/klient.js";
import { useOnline } from "../../api/online.js";
import { abgleichen } from "../../lokal/abgleich.js";
import { useStatusArten } from "../../lokal/bestand.js";
import {
  Eingabe,
  Farbpunkt,
  Karte,
  Knopf,
  NurMitNetz,
  Rueckmeldung,
  Schalter,
} from "../../ui/bausteine.jsx";

/**
 * Eine Farbtafel mit Farben, die sich auch bei Sonne und im Dunkeln
 * unterscheiden lassen. Wer eine andere will, nimmt den Farbwaehler.
 */
const FARBTAFEL = [
  { farbe: "#16a34a", name: "Grün" },
  { farbe: "#65a30d", name: "Hellgrün" },
  { farbe: "#ca8a04", name: "Gelb" },
  { farbe: "#ea580c", name: "Orange" },
  { farbe: "#dc2626", name: "Rot" },
  { farbe: "#9f1239", name: "Weinrot" },
  { farbe: "#7c3aed", name: "Violett" },
  { farbe: "#2563eb", name: "Blau" },
  { farbe: "#0891b2", name: "Türkis" },
  { farbe: "#737373", name: "Grau" },
  { farbe: "#a3a3a3", name: "Hellgrau" },
  { farbe: "#1f2937", name: "Schwarz" },
] as const;

type Meldung = { art: "gut" | "warnung"; text: string } | null;

/** Aus einem Namen den Schluessel machen: „In Reparatur" → `in_reparatur`. */
function schluesselAus(name: string): string {
  const roh = name
    .toLowerCase()
    .replaceAll("ä", "ae")
    .replaceAll("ö", "oe")
    .replaceAll("ü", "ue")
    .replaceAll("ß", "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 40);
  return /^[a-z]/.test(roh) ? roh : `s_${roh}`.slice(0, 40);
}

const VERBLEIB_NAMEN = ["nicht_erfasst", "im_lager", "im_einsatz", "ausgeliehen"];

export function StatusVerwaltung() {
  const online = useOnline();
  const arten = useStatusArten();
  const [meldung, setMeldung] = useState<Meldung>(null);
  const [offen, setOffen] = useState<string | null>(null);
  const zwischenspeicher = useQueryClient();

  const aendern = useMutation({
    mutationFn: ({ schluessel, aenderung }: { schluessel: string; aenderung: object }) =>
      anfrage<StatusArt>("PATCH", `/api/core/status-arten/${schluessel}`, aenderung),
    onSuccess: (art) => {
      setMeldung({ art: "gut", text: `✓ ${art.bezeichnung} gespeichert` });
      // Aendert sich ein Kennzeichen, rechnet der Server den Bestand neu --
      // also alles neu holen, nicht nur die Liste der Status. Die lokale
      // Kopie rechnet nach dem Stammabruf ebenfalls alles neu.
      void zwischenspeicher.invalidateQueries();
      void abgleichen({ stammdaten: true });
    },
    onError: (f) => {
      setMeldung({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });

  if (sitzungLesen()?.benutzer.rolle !== "administrator") {
    return <Rueckmeldung art="warnung" kopf="Nur für Administratoren" />;
  }

  const verschieben = (index: number, richtung: -1 | 1): void => {
    const a = arten[index];
    const b = arten[index + richtung];
    if (a === undefined || b === undefined) return;
    // Tauschen. Liegen beide gleichauf, bekommt der eine einen Schritt Abstand.
    const rA = b.reihenfolge === a.reihenfolge ? a.reihenfolge + richtung : b.reihenfolge;
    aendern.mutate({ schluessel: a.schluessel, aenderung: { reihenfolge: rA } });
    aendern.mutate({ schluessel: b.schluessel, aenderung: { reihenfolge: a.reihenfolge } });
  };

  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold">Status und Farben</h1>
      <p className="text-neutral-600 dark:text-neutral-300">
        Der Status sagt, in welchem Zustand ein Artikel ist — unabhängig davon, wo er liegt.
        Verschieben ändert ihn nie.
      </p>
      {!online && <NurMitNetz />}
      {meldung !== null && <Rueckmeldung art={meldung.art} kopf={meldung.text} />}

      <ul className="flex flex-col gap-2">
        {arten.map((art, i) => (
          <li
            key={art.schluessel}
            className={`rounded-xl bg-white dark:bg-neutral-800 ${art.aktiv ? "" : "opacity-60"}`}
          >
            <button
              type="button"
              className="flex min-h-14 w-full items-center gap-3 px-4 py-2 text-left"
              aria-expanded={offen === art.schluessel}
              onClick={() => {
                setOffen(offen === art.schluessel ? null : art.schluessel);
              }}
            >
              <Farbpunkt farbe={art.farbe} gross />
              <span className="flex flex-1 flex-col">
                <span className="font-semibold">{art.bezeichnung}</span>
                <span className="flex flex-wrap gap-1 text-xs">
                  {art.standard && <Schild>Standard</Schild>}
                  {art.einsatzbereit && <Schild>zählt als einsatzbereit</Schild>}
                  {art.archiviert && <Schild>archiviert</Schild>}
                  {art.ort_unbekannt && <Schild>nach Unbekannt</Schild>}
                  {verhaltenVon(art).nur_admin && <Schild>nur Admin</Schild>}
                  {verhaltenVon(art).mit_bestaetigung && <Schild>mit Bestätigung</Schild>}
                  {verhaltenVon(art).rueckbuchung_ignorieren && <Schild>bleibt liegen</Schild>}
                  {!art.aktiv && <Schild>stillgelegt</Schild>}
                </span>
              </span>
            </button>

            {offen === art.schluessel && (
              <StatusBearbeiten
                art={art}
                online={online}
                speichern={(aenderung) => {
                  aendern.mutate({ schluessel: art.schluessel, aenderung });
                }}
                hoch={
                  i > 0
                    ? () => {
                        verschieben(i, -1);
                      }
                    : null
                }
                runter={
                  i < arten.length - 1
                    ? () => {
                        verschieben(i, 1);
                      }
                    : null
                }
              />
            )}
          </li>
        ))}
      </ul>

      <NeuerStatus
        online={online}
        vorhandene={arten}
        melden={setMeldung}
        naechsteReihenfolge={Math.max(0, ...arten.map((a) => a.reihenfolge)) + 10}
      />
    </div>
  );
}

function Schild({ children }: { children: string }) {
  return (
    <span className="rounded-full bg-neutral-200 px-2 py-0.5 font-semibold text-neutral-700 dark:bg-neutral-700 dark:text-neutral-200">
      {children}
    </span>
  );
}

/** Farbtafel und freier Farbwaehler. */
function FarbWahl({ farbe, aendern }: { farbe: string; aendern: (neu: string) => void }) {
  return (
    <fieldset className="flex flex-col gap-2">
      <legend className="mb-1 text-sm font-semibold">Farbe</legend>
      <div className="flex flex-wrap gap-2">
        {FARBTAFEL.map((f) => (
          <button
            key={f.farbe}
            type="button"
            title={f.name}
            aria-label={f.name}
            aria-pressed={farbe === f.farbe}
            className={`size-12 rounded-full ring-offset-2 ring-offset-white dark:ring-offset-neutral-800 ${
              farbe === f.farbe ? "ring-4 ring-blue-700" : "ring-1 ring-neutral-300"
            }`}
            style={{ backgroundColor: f.farbe }}
            onClick={() => {
              aendern(f.farbe);
            }}
          />
        ))}
      </div>
      <label className="flex min-h-12 items-center gap-3">
        <input
          type="color"
          className="h-12 w-16 cursor-pointer rounded-lg"
          value={farbe}
          onChange={(e) => {
            aendern(e.target.value.toLowerCase());
          }}
        />
        <span className="text-sm">Eigene Farbe wählen</span>
      </label>
    </fieldset>
  );
}

interface KennzeichenWerte {
  einsatzbereit: boolean;
  archiviert: boolean;
  ort_unbekannt: boolean;
}

/**
 * Die Kennzeichen. Archiviert und einsatzbereit schliessen sich aus; nach
 * Unbekannt fuehrt nur ein archivierender Status (ADR 0020).
 */
function Kennzeichen({
  einsatzbereit,
  archiviert,
  ort_unbekannt,
  aendern,
  gesperrt,
}: KennzeichenWerte & {
  aendern: (k: KennzeichenWerte) => void;
  gesperrt: boolean;
}) {
  return (
    <>
      <Schalter
        beschriftung="Zählt als einsatzbereit"
        hinweis="Nur solche Artikel zählen zum verfügbaren Bestand."
        an={einsatzbereit}
        gesperrt={gesperrt}
        aendern={(an) => {
          aendern({
            einsatzbereit: an,
            archiviert: an ? false : archiviert,
            ort_unbekannt: an ? false : ort_unbekannt,
          });
        }}
      />
      <Schalter
        beschriftung="Archiviert"
        hinweis="Fällt aus allen aktiven Listen, etwa verloren oder gestohlen. Beim Einbuchen gilt wieder der Standard."
        an={archiviert}
        gesperrt={gesperrt}
        aendern={(an) => {
          aendern({
            archiviert: an,
            einsatzbereit: an ? false : einsatzbereit,
            // Nach Unbekannt fuehrt nur ein archivierender Status (ADR 0020).
            ort_unbekannt: an ? ort_unbekannt : false,
          });
        }}
      />
      <Schalter
        beschriftung="Ort wird unbekannt"
        hinweis="Der Artikel kommt nach „Unbekannt“ und verschwindet aus seinem Packstück – etwa verloren oder gestohlen. Nur bei archivierenden Status."
        an={ort_unbekannt}
        gesperrt={gesperrt || !archiviert}
        aendern={(an) => {
          aendern({ einsatzbereit, archiviert, ort_unbekannt: an });
        }}
      />
    </>
  );
}

interface VerhaltenWerte {
  nur_admin: boolean;
  mit_bestaetigung: boolean;
  rueckbuchung_ignorieren: boolean;
}

/** Die drei Haken aus ADR 0025. Eine lokale Kopie von vor Vertrag 2.10 kennt sie nicht. */
function verhaltenVon(art: StatusArt | undefined): VerhaltenWerte {
  return {
    nur_admin: art?.nur_admin === true,
    mit_bestaetigung: art?.mit_bestaetigung === true,
    rueckbuchung_ignorieren: art?.rueckbuchung_ignorieren === true,
  };
}

/**
 * Rechte und Verhalten (ADR 0025). Der Standard kann nicht „Nur Admin“ sein,
 * sonst setzt ihn kein Helfer mehr.
 */
function Verhalten({
  werte,
  aendern,
  standard,
}: {
  werte: VerhaltenWerte;
  aendern: (w: VerhaltenWerte) => void;
  standard: boolean;
}) {
  return (
    <>
      <Schalter
        beschriftung="Nur Admin"
        hinweis={
          standard
            ? "Nicht beim Standard – den muss jeder setzen können."
            : "Nur ein Administrator darf diesen Status setzen, und nur mit Netz."
        }
        an={werte.nur_admin}
        gesperrt={standard}
        aendern={(an) => {
          aendern({ ...werte, nur_admin: an });
        }}
      />
      <Schalter
        beschriftung="Mit Bestätigung"
        hinweis="Vor dem Setzen muss man ein zweites Mal tippen."
        an={werte.mit_bestaetigung}
        aendern={(an) => {
          aendern({ ...werte, mit_bestaetigung: an });
        }}
      />
      <Schalter
        beschriftung="Stammplatz-Rückbuchung ignorieren"
        hinweis="„Alles da“ und „Wieder da“ lassen solche Geräte liegen, etwa defekt oder in Prüfung."
        an={werte.rueckbuchung_ignorieren}
        aendern={(an) => {
          aendern({ ...werte, rueckbuchung_ignorieren: an });
        }}
      />
    </>
  );
}

function StatusBearbeiten({
  art,
  online,
  speichern,
  hoch,
  runter,
}: {
  art: StatusArt;
  online: boolean;
  speichern: (aenderung: Partial<StatusArt>) => void;
  hoch: (() => void) | null;
  runter: (() => void) | null;
}) {
  const [bezeichnung, setBezeichnung] = useState(art.bezeichnung);
  const [farbe, setFarbe] = useState(art.farbe);
  const [kennzeichen, setKennzeichen] = useState<KennzeichenWerte>({
    einsatzbereit: art.einsatzbereit,
    archiviert: art.archiviert,
    // Eine lokale Kopie von vor Vertrag 2.9 kennt das Feld nicht.
    ort_unbekannt: (art.ort_unbekannt as boolean | undefined) === true,
  });
  const [verhalten, setVerhalten] = useState<VerhaltenWerte>(verhaltenVon(art));
  const vorher = verhaltenVon(art);

  const aenderung: Partial<StatusArt> = {
    ...(bezeichnung.trim() !== art.bezeichnung ? { bezeichnung: bezeichnung.trim() } : {}),
    ...(farbe !== art.farbe ? { farbe } : {}),
    ...(kennzeichen.einsatzbereit !== art.einsatzbereit
      ? { einsatzbereit: kennzeichen.einsatzbereit }
      : {}),
    ...(kennzeichen.archiviert !== art.archiviert ? { archiviert: kennzeichen.archiviert } : {}),
    ...(kennzeichen.ort_unbekannt !== ((art.ort_unbekannt as boolean | undefined) === true)
      ? { ort_unbekannt: kennzeichen.ort_unbekannt }
      : {}),
    ...(verhalten.nur_admin !== vorher.nur_admin ? { nur_admin: verhalten.nur_admin } : {}),
    ...(verhalten.mit_bestaetigung !== vorher.mit_bestaetigung
      ? { mit_bestaetigung: verhalten.mit_bestaetigung }
      : {}),
    ...(verhalten.rueckbuchung_ignorieren !== vorher.rueckbuchung_ignorieren
      ? { rueckbuchung_ignorieren: verhalten.rueckbuchung_ignorieren }
      : {}),
  };
  const geaendert = Object.keys(aenderung).length > 0;

  return (
    <div className="flex flex-col gap-4 border-t border-neutral-200 p-4 dark:border-neutral-700">
      <Eingabe beschriftung="Name" wert={bezeichnung} aendern={setBezeichnung} />
      <FarbWahl farbe={farbe} aendern={setFarbe} />
      <Kennzeichen
        {...kennzeichen}
        aendern={setKennzeichen}
        // Der Standard darf nicht archivieren -- ein neuer Artikel waere sonst
        // sofort aus allen Listen verschwunden.
        gesperrt={art.standard}
      />
      <Verhalten werte={verhalten} aendern={setVerhalten} standard={art.standard} />
      {art.standard && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Das ist der Standard: den Status hat jeder Artikel, bis jemand einen anderen setzt.
        </p>
      )}

      <Knopf
        art="haupt"
        disabled={!online || !geaendert || bezeichnung.trim() === ""}
        onClick={() => {
          speichern(aenderung);
        }}
      >
        Speichern
      </Knopf>

      <div className="flex flex-wrap gap-2">
        {hoch !== null && (
          <Knopf disabled={!online} onClick={hoch}>
            ▲ Nach oben
          </Knopf>
        )}
        {runter !== null && (
          <Knopf disabled={!online} onClick={runter}>
            ▼ Nach unten
          </Knopf>
        )}
        {!art.standard && art.aktiv && !art.archiviert && !vorher.nur_admin && (
          <Knopf
            disabled={!online}
            onClick={() => {
              speichern({ standard: true });
            }}
          >
            Zum Standard machen
          </Knopf>
        )}
        {!art.standard && (
          <Knopf
            art="still"
            disabled={!online}
            onClick={() => {
              speichern({ aktiv: !art.aktiv });
            }}
          >
            {art.aktiv ? "Stilllegen" : "Wieder aktivieren"}
          </Knopf>
        )}
      </div>
      {!art.standard && art.aktiv && (
        <p className="text-sm text-neutral-500 dark:text-neutral-400">
          Stillgelegt lässt sich der Status nicht mehr setzen. Artikel, die ihn haben, behalten ihn,
          und die Historie bleibt lesbar.
        </p>
      )}
    </div>
  );
}

function NeuerStatus({
  online,
  vorhandene,
  melden,
  naechsteReihenfolge,
}: {
  online: boolean;
  vorhandene: readonly StatusArt[];
  melden: (m: Meldung) => void;
  naechsteReihenfolge: number;
}) {
  const [offen, setOffen] = useState(false);
  const [bezeichnung, setBezeichnung] = useState("");
  const [farbe, setFarbe] = useState<string>(FARBTAFEL[7].farbe);
  const [kennzeichen, setKennzeichen] = useState<KennzeichenWerte>({
    einsatzbereit: false,
    archiviert: false,
    ort_unbekannt: false,
  });
  const [verhalten, setVerhalten] = useState<VerhaltenWerte>(verhaltenVon(undefined));

  const schluessel = schluesselAus(bezeichnung);
  const vergeben =
    vorhandene.some((a) => a.schluessel === schluessel) || VERBLEIB_NAMEN.includes(schluessel);

  const anlegen = useMutation({
    mutationFn: () =>
      anfrage<StatusArt>("POST", "/api/core/status-arten", {
        schluessel,
        bezeichnung: bezeichnung.trim(),
        farbe,
        ...kennzeichen,
        ...verhalten,
        reihenfolge: naechsteReihenfolge,
      }),
    onSuccess: (art) => {
      melden({ art: "gut", text: `✓ ${art.bezeichnung} angelegt` });
      setBezeichnung("");
      setKennzeichen({ einsatzbereit: false, archiviert: false, ort_unbekannt: false });
      setVerhalten(verhaltenVon(undefined));
      setOffen(false);
      void abgleichen({ stammdaten: true });
    },
    onError: (f) => {
      melden({ art: "warnung", text: f instanceof Error ? f.message : String(f) });
    },
  });

  if (!offen) {
    return (
      <Knopf
        art="haupt"
        disabled={!online}
        onClick={() => {
          setOffen(true);
        }}
      >
        Neuen Status anlegen
      </Knopf>
    );
  }

  return (
    <Karte titel="Neuer Status">
      <Eingabe
        beschriftung="Name"
        wert={bezeichnung}
        aendern={setBezeichnung}
        hinweis={
          schluessel.length >= 2
            ? vergeben
              ? "Diesen Namen gibt es schon."
              : `Wird gespeichert als „${schluessel}“ — das bleibt, auch wenn der Name sich später ändert.`
            : undefined
        }
      />
      <FarbWahl farbe={farbe} aendern={setFarbe} />
      <Kennzeichen {...kennzeichen} aendern={setKennzeichen} gesperrt={false} />
      <Verhalten werte={verhalten} aendern={setVerhalten} standard={false} />
      <div className="flex gap-2">
        <Knopf
          art="haupt"
          disabled={!online || schluessel.length < 2 || vergeben || anlegen.isPending}
          onClick={() => {
            anlegen.mutate();
          }}
        >
          Anlegen
        </Knopf>
        <Knopf
          art="still"
          onClick={() => {
            setOffen(false);
          }}
        >
          Abbrechen
        </Knopf>
      </div>
    </Karte>
  );
}
